import { Attachment } from "@/src/components/ui-kit/attachment-viewer";
import { useAuth } from "@/src/contexts/auth.context";
import { usePermissions } from "@/src/hooks/use-permissions";
import { BlocksApiArticle } from "@/src/models/wiki.models";
import { useGlobalNewsQuery } from "@/src/services/api/news.api";
import {
  useToggleWikiSaved,
  useWikiArticle,
  useWikiCategoriesQuery,
  useWikiDeleteArticle,
  useWikiSaveState,
} from "@/src/services/wiki/wiki.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { router, useLocalSearchParams } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatUserName } from "@/src/utils/string.utils";

const themeColors = {
  light: {
    slateDefault: "#526581",
    onyxDefault: "#22272a",
    chalkDefault: "#FFFFFF",
    obsidianDefault: "#F2F4F6",
    ash3: "#DFE5EC",
  },
  dark: {
    slateDefault: "#8aaac8",
    onyxDefault: "#D7DADC",
    chalkDefault: "#0D1416",
    obsidianDefault: "#0f1113",
    ash3: "#2d3540",
  },
} as const;

export function useWikiArticleDetail() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canUpdate, canDelete, isSuperAdmin } = usePermissions();
  const userId = user?.itemId ?? "";
  const { primaryPalette, resolvedScheme } = useTheme();
  const colors = themeColors[resolvedScheme];

  const { articleId } = useLocalSearchParams<{ articleId: string }>();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    data: article,
    isLoading,
    isError,
  } = useWikiArticle(articleId ?? undefined, !!articleId);

  const { data: categories = [] } = useWikiCategoriesQuery();
  const { isSaved } = useWikiSaveState();
  const { mutate: toggleSave } = useToggleWikiSaved(userId);

  const {
    data: globalNewsData,
    isLoading: isLoadingGlobalNews,
    isError: isGlobalNewsError,
  } = useGlobalNewsQuery();

  const { mutate: deleteArticle } = useWikiDeleteArticle({
    onSuccess: () => {
      setShowDeleteConfirm(false);
      router.back();
    },
  });

  const isOwner = !!userId && article?.CreatedBy === userId;

  // categoryIds is a single string on BlocksApiArticle, not an array
  const categoryNames = article?.categoryIds
    ? categories
        .filter((cat) => cat.categoryID === article.categoryIds)
        .map((cat) => cat.name)
    : [];

  const attachments: Attachment[] = (article?.attachmentUrls ?? []).map(
    (a) => ({
      url: a.url,
      type: a.mimeType?.startsWith("image/") ? "image" : "file",
    }),
  );

  const resolveAuthorName = (a: BlocksApiArticle): string => {
    if (!a.authorSnapshot) return a.CreatedBy ?? "";
    const full = formatUserName(
      a.authorSnapshot.firstName,
      a.authorSnapshot.lastName,
    );
    return (full || a.CreatedBy) ?? "";
  };

  const handleEdit = () => {
    if (!article) return;
    router.push({
      pathname: "/(tabs)/wiki/wiki-creation",
      params: {
        draftId: article.ItemId,
        Title: article.Title,
        Content: article.Content,
        categoryId: article.categoryIds ?? "",
        coverImageUrl: article.coverImageUrl ?? "",
        attachments: JSON.stringify(article.attachmentUrls ?? []),
      },
    });
  };

  const dropdownItems = article
    ? [
        {
          label: isSaved(article.ItemId) ? t("BTN_UNSAVE") : t("BTN_SAVE"),
          onPress: () => toggleSave(article.ItemId),
        },
        { label: t("LABEL_SHARE"), onPress: () => setShowShareModal(true) },
        ...(isOwner && canUpdate("wiki")
          ? [{ label: t("BTN_UPDATE"), onPress: handleEdit }]
          : []),
        ...((isOwner || isSuperAdmin) && canDelete("wiki")
          ? [
              {
                label: t("BTN_DELETE"),
                onPress: () => setShowDeleteConfirm(true),
                destructive: true,
              },
            ]
          : []),
      ]
    : [];

  return {
    t,
    article: article ?? null,
    isLoading,
    isError,
    primaryPalette,
    colors,
    resolvedScheme,
    categoryNames,
    attachments,
    dropdownItems,
    resolveAuthorName,
    globalNewsData,
    isLoadingGlobalNews,
    isGlobalNewsError,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showShareModal,
    setShowShareModal,
    deleteArticle,
  };
}
